import React, { useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import styles from "./JobDescription.module.css";
import dummyJobs from "../../../data/jobs";
import {
  FiMapPin, FiBriefcase, FiClock, FiDollarSign, FiBookmark,
  FiShare2, FiArrowLeft, FiCheck, FiUsers, FiGlobe, FiCalendar,
  FiChevronRight
} from "react-icons/fi";

const JobDescription = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const job = state?.job || dummyJobs.find((j) => j.id === parseInt(id)) || dummyJobs[0];
  const similarJobs = dummyJobs.filter((j) => j.id !== job.id && j.tags.some((t) => job.tags.includes(t))).slice(0, 4);

  const handleApply = () => {
    setApplied(true);
    setTimeout(() => setApplied(false), 3000);
  };

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <div className={styles.breadcrumbInner}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back to Jobs
          </button>
          <div className={styles.breadcrumbLinks}>
            <Link to="/">Home</Link>
            <FiChevronRight />
            <Link to="/find-jobs">Find Jobs</Link>
            <FiChevronRight />
            <span>{job.title}</span>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {/* Left: Main Content */}
        <div className={styles.main}>
          {/* Job Header */}
          <div className={styles.jobHeader}>
            <div className={styles.headerTop}>
              <div
                className={styles.companyLogo}
                style={{ background: job.companyColor + "18", color: job.companyColor }}
              >
                {job.companyInitial}
              </div>
              <div className={styles.headerInfo}>
                <h1 className={styles.jobTitle}>{job.title}</h1>
                <div className={styles.jobMeta}>
                  <span className={styles.company}>{job.company}</span>
                  <span className={styles.metaSep}>·</span>
                  <span className={styles.metaItem}><FiMapPin size={14} /> {job.location}</span>
                  <span className={styles.metaSep}>·</span>
                  <span className={styles.metaItem}><FiClock size={14} /> Posted {job.postedDays === 0 ? "today" : `${job.postedDays}d ago`}</span>
                </div>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={`${styles.saveBtn} ${saved ? styles.saveBtnActive : ""}`}
                  onClick={() => setSaved(!saved)}
                >
                  <FiBookmark />
                </button>
                <button className={styles.shareBtn}>
                  <FiShare2 />
                </button>
              </div>
            </div>

            <div className={styles.jobBadges}>
              <span className={styles.badge}><FiBriefcase size={13} /> {job.type}</span>
              <span className={`${styles.badge} ${styles.badgeGreen}`}>{job.mode}</span>
              <span className={styles.badge}>{job.experience}</span>
              <span className={`${styles.badge} ${styles.badgePurple}`}><FiDollarSign size={13} /> {job.salary}</span>
            </div>

            <div className={styles.applyRow}>
              <button
                className={`${styles.applyBtn} ${applied ? styles.applyBtnSuccess : ""}`}
                onClick={handleApply}
                disabled={applied}
              >
                {applied ? <><FiCheck /> Application Sent!</> : "Apply Now"}
              </button>
              <span className={styles.applicants}><FiUsers size={14} /> 124 applicants</span>
            </div>
          </div>

          {/* Tabs */}
          <div className={styles.tabs}>
            {["overview", "requirements", "benefits"].map((tab) => (
              <button
                key={tab}
                className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {activeTab === "overview" && (
              <div>
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>About the Role</h3>
                  <p className={styles.sectionText}>{job.description}</p>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Responsibilities</h3>
                  <ul className={styles.bulletList}>
                    {job.responsibilities.map((r, i) => (
                      <li key={i}>
                        <span className={styles.bullet}><FiCheck /></span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </section>

                <div className={styles.tagsSection}>
                  <h3 className={styles.sectionTitle}>Skills Required</h3>
                  <div className={styles.skillTags}>
                    {[...job.tags, job.experience, job.type, job.mode].map((tag) => (
                      <span key={tag} className={styles.skillTag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "requirements" && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Requirements</h3>
                <ul className={styles.bulletList}>
                  {job.requirements.map((r, i) => (
                    <li key={i}>
                      <span className={styles.bullet}><FiCheck /></span>
                      {r}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {activeTab === "benefits" && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Benefits & Perks</h3>
                <div className={styles.benefitsGrid}>
                  {job.benefits.map((b, i) => (
                    <div key={i} className={styles.benefitCard}>
                      <FiCheck className={styles.benefitCheck} />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Apply CTA */}
          <div className={styles.applyCta}>
            <p>Interested in this position?</p>
            <button
              className={`${styles.applyBtn} ${applied ? styles.applyBtnSuccess : ""}`}
              onClick={handleApply}
              disabled={applied}
            >
              {applied ? <><FiCheck /> Application Sent!</> : "Apply for this Job"}
            </button>
          </div>
        </div>

        {/* Right: Sidebar */}
        <aside className={styles.sidebar}>
          <div className={styles.companyCard}>
            <div
              className={styles.companyCardLogo}
              style={{ background: job.companyColor + "18", color: job.companyColor }}
            >
              {job.companyInitial}
            </div>
            <h3 className={styles.companyName}>{job.company}</h3>
            <p className={styles.companyIndustry}>{job.industry}</p>

            <div className={styles.companyDetails}>
              <div className={styles.companyDetail}>
                <FiUsers className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Company Size</div>
                  <div className={styles.detailValue}>{job.companySize}</div>
                </div>
              </div>
              <div className={styles.companyDetail}>
                <FiCalendar className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Founded</div>
                  <div className={styles.detailValue}>{job.founded}</div>
                </div>
              </div>
              <div className={styles.companyDetail}>
                <FiGlobe className={styles.detailIcon} />
                <div>
                  <div className={styles.detailLabel}>Website</div>
                  <div className={styles.detailValue}>{job.website}</div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.jobOverviewCard}>
            <h4 className={styles.overviewTitle}>Job Overview</h4>
            <div className={styles.overviewItems}>
              <div className={styles.overviewItem}>
                <FiBriefcase className={styles.overviewIcon} />
                <div>
                  <div className={styles.overviewLabel}>Job Type</div>
                  <div className={styles.overviewValue}>{job.type}</div>
                </div>
              </div>
              <div className={styles.overviewItem}>
                <FiMapPin className={styles.overviewIcon} />
                <div>
                  <div className={styles.overviewLabel}>Location</div>
                  <div className={styles.overviewValue}>{job.location}</div>
                </div>
              </div>
              <div className={styles.overviewItem}>
                <FiDollarSign className={styles.overviewIcon} />
                <div>
                  <div className={styles.overviewLabel}>Salary</div>
                  <div className={styles.overviewValue}>{job.salary}</div>
                </div>
              </div>
              <div className={styles.overviewItem}>
                <FiUsers className={styles.overviewIcon} />
                <div>
                  <div className={styles.overviewLabel}>Experience</div>
                  <div className={styles.overviewValue}>{job.experience}</div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Similar Jobs */}
      {similarJobs.length > 0 && (
        <div className={styles.similarSection}>
          <div className={styles.similarInner}>
            <h2 className={styles.similarTitle}>Similar Jobs</h2>
            <div className={styles.similarGrid}>
              {similarJobs.map((sj) => (
                <div
                  key={sj.id}
                  className={styles.similarCard}
                  onClick={() => navigate(`/job-desc/${sj.id}`, { state: { job: sj } })}
                >
                  <div className={styles.similarCardTop}>
                    <div
                      className={styles.similarLogo}
                      style={{ background: sj.companyColor + "18", color: sj.companyColor }}
                    >
                      {sj.companyInitial}
                    </div>
                    <div>
                      <div className={styles.similarJobTitle}>{sj.title}</div>
                      <div className={styles.similarCompany}>{sj.company} · {sj.location}</div>
                    </div>
                  </div>
                  <div className={styles.similarTags}>
                    <span className={styles.simTag}>{sj.type}</span>
                    <span className={styles.simTag}>{sj.mode}</span>
                  </div>
                  <div className={styles.similarSalary}>{sj.salary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescription;
